/**
 * CustomerSearchAutocomplete Component
 * Searchable dropdown with inline customer creation (similar to Xero)
 */

import React, { useState, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Add, Person } from "@mui/icons-material";
import { searchCustomers, createCustomer } from "../../features/customers/services/customerService.js";

/**
 * CustomerSearchAutocomplete Component
 * @param {Object} props
 * @param {Object|null} props.value - Selected customer object
 * @param {Function} props.onChange - Callback when customer is selected/changed
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.required] - Whether field is required
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {Object} [props.error] - Error state
 */
const CustomerSearchAutocomplete = ({
  value,
  onChange,
  label = "Customer",
  required = false,
  disabled = false,
  error = null,
  initialInputValue = "",
  onSearchTextChange,
}) => {
  const [inputValue, setInputValue] = useState(initialInputValue || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  React.useEffect(() => {
    if (initialInputValue && initialInputValue !== inputValue) {
      setInputValue(initialInputValue);
    }
    // Only react to an external hint (e.g. bank payer name), not keystrokes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInputValue]);

  // Debounced search function
  const search = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const results = await searchCustomers(searchTerm);
        setOptions(results);
      } catch (err) {
        console.error("Error searching customers:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Handle input change with debounce
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue) {
        search(inputValue);
      } else {
        setOptions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, search]);

  // Handle creating new customer
  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      setCreateError("Customer name is required");
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const result = await createCustomer({
        name: newCustomerName.trim(),
        phone: null, // Will be set to placeholder, user can update later
      });

      if (result.success) {
        const newCustomer = result.data;
        onChange(newCustomer);
        setCreateDialogOpen(false);
        setNewCustomerName("");
        setInputValue(newCustomer.name);
      } else {
        setCreateError(result.error || "Failed to create customer");
      }
    } catch (err) {
      setCreateError(err.message || "Failed to create customer");
    } finally {
      setCreating(false);
    }
  };

  // Check if we should show "Add new" option
  const showAddNewOption =
    inputValue.trim().length >= 2 &&
    !options.some(
      (opt) => opt.name?.toLowerCase() === inputValue.toLowerCase()
    );

  // Combined options including "Add new" option
  const allOptions = showAddNewOption
    ? [
        {
          _id: "__NEW__",
          name: `Add "${inputValue}"`,
          isNew: true,
        },
        ...options,
      ]
    : options;

  return (
    <>
      <Autocomplete
        disabled={disabled}
        value={value}
        onChange={(event, newValue) => {
          if (newValue && newValue.isNew) {
            // User wants to create new customer
            setNewCustomerName(inputValue.trim());
            setCreateDialogOpen(true);
          } else {
            onChange(newValue);
          }
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          if (onSearchTextChange) {
            onSearchTextChange(newInputValue);
          }
        }}
        options={allOptions}
        getOptionLabel={(option) => {
          if (option.isNew) return option.name;
          return option.name || "";
        }}
        isOptionEqualToValue={(option, value) => {
          return (
            option._id === value?._id ||
            option.customerId === value?.customerId
          );
        }}
        loading={loading}
        filterOptions={(x) => x} // Disable client-side filtering, we do server-side
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          if (option.isNew) {
            return (
              <Box
                component="li"
                {...props}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                  px: 2,
                  borderTop: "1px solid",
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.action.hover,
                })}
              >
                <Add color="primary" />
                <Typography variant="body2" color="primary">
                  {option.name}
                </Typography>
              </Box>
            );
          }

          return (
            <Box component="li" {...props} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option.name}
                  </Typography>
                  {option.phone && !option.phone.startsWith("TEMP_") && (
                    <Typography variant="caption" color="text.secondary">
                      {option.phone}
                    </Typography>
                  )}
                  {option.email && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        }}
        noOptionsText={
          inputValue.trim().length < 2
            ? "Type at least 2 characters to search"
            : "No customers found"
        }
      />

      {/* Create Customer Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          if (!creating) {
            setCreateDialogOpen(false);
            setNewCustomerName("");
            setCreateError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Customer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Customer Name"
            fullWidth
            variant="outlined"
            value={newCustomerName}
            onChange={(e) => {
              setNewCustomerName(e.target.value);
              setCreateError(null);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter" && newCustomerName.trim()) {
                handleCreateCustomer();
              }
            }}
            error={!!createError}
            helperText={
              createError || "Enter the customer name to create a new contact"
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewCustomerName("");
              setCreateError(null);
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!newCustomerName.trim() || creating}
          >
            {creating ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerSearchAutocomplete;

